import { Controller } from "@hotwired/stimulus"
import { getJSON, listResults } from "../api.js"

/**
 * Chip input dla wolnotekstowych tagów z podpowiedziami (typeahead) po
 * istniejących tagach z /api/tags/?q=.
 *
 * Jedna instancja = jedna podkategoria (data-tag-chips-subcategory-value).
 * Kreator (wizard) czyta bieżące tagi przez getter `names` albo nasłuchując
 * zdarzenia "tag-chips:change" na window (detail: { subcategory, names }).
 *
 * Dodawanie: Enter (podświetlona podpowiedź albo surowy tekst z inputu) lub
 * kliknięcie podpowiedzi. Dedupe: case-sensitive, po trim. Usuwanie: × na chipie.
 *
 * XSS hardening: zero innerHTML — cały DOM przez createElement/textContent.
 *
 * @target input       - pole tekstowe do wpisywania tagu / wyszukiwania podpowiedzi
 * @target chips        - kontener na chipy wybranych tagów
 * @target suggestions  - lista podpowiedzi (position: absolute, ls-combo__list)
 * @value  subcategory  - identyfikator podkategorii, do której należy ten input
 * @value  debounceMs   - opóźnienie debounce dla typeahead (domyślnie 220)
 * @value  maxSuggestions - cap liczby podpowiedzi (domyślnie 8)
 */
export default class extends Controller {
  static targets = ["input", "chips", "suggestions"]
  static values = {
    subcategory: String,
    debounceMs: { type: Number, default: 220 },
    maxSuggestions: { type: Number, default: 8 },
  }

  connect() {
    this._names = []
    this._timer = null
    this._items = []
    this._hiIndex = -1
    if (!this.inputTarget.placeholder) {
      this.inputTarget.placeholder = "Tagi w tej podkategorii"
    }
    this.#renderChips()
  }

  disconnect() {
    clearTimeout(this._timer)
  }

  // Bieżące nazwy tagów (kopia — wołający nie może mutować stanu kontrolera).
  get names() {
    return [...this._names]
  }

  /**
   * Ustawia bieżące tagi z zewnątrz (np. wypełnienie modala edycji z API) — zastępuje
   * całą listę, renderuje chipy i emituje tag-chips:change (spójnie z dodawaniem/
   * usuwaniem), więc kod nasłuchujący zdarzenia dostaje ten sam sygnał co przy edycji
   * ręcznej. Trim + dedupe case-sensitive, tak jak przy dodawaniu pojedynczego tagu.
   * @param {string[]} names
   */
  setNames(names) {
    const list = Array.isArray(names) ? names : []
    const deduped = []
    for (const raw of list) {
      const name = String(raw ?? "").trim()
      if (name && !deduped.includes(name)) deduped.push(name)
    }
    this._names = deduped
    this.#renderChips()
    this.#emit()
  }

  // ---- akcje (data-action) ----

  // Zdarzenie: input — debounce, potem fetch podpowiedzi
  search() {
    clearTimeout(this._timer)
    const q = this.inputTarget.value.trim()
    if (!q) {
      this.#closeSuggestions()
      return
    }
    this._timer = setTimeout(() => this.#fetchSuggestions(q), this.debounceMsValue)
  }

  keydown(e) {
    const suggestionsOpen = !this.suggestionsTarget.hidden
    if (e.key === "ArrowDown" && suggestionsOpen) {
      e.preventDefault()
      this._hiIndex = Math.min(this._hiIndex + 1, this._items.length - 1)
      this.#highlight()
    } else if (e.key === "ArrowUp" && suggestionsOpen) {
      e.preventDefault()
      this._hiIndex = Math.max(this._hiIndex - 1, 0)
      this.#highlight()
    } else if (e.key === "Enter") {
      e.preventDefault()
      if (suggestionsOpen && this._hiIndex >= 0 && this._items[this._hiIndex]) {
        this.#add(this._items[this._hiIndex].name)
      } else {
        this.#add(this.inputTarget.value)
      }
    } else if (e.key === "Escape") {
      this.#closeSuggestions()
    } else if (e.key === "Backspace" && !this.inputTarget.value && this._names.length) {
      this.#remove(this._names[this._names.length - 1])
    }
  }

  // Zdarzenie: blur — zamknij z opóźnieniem (pozwala obsłużyć mousedown na podpowiedzi)
  close() {
    setTimeout(() => this.#closeSuggestions(), 140)
  }

  // ---- prywatne ----

  /** Pobiera podpowiedzi z /api/tags/?q=, filtruje już wybrane nazwy. */
  async #fetchSuggestions(q) {
    let data
    try {
      data = await getJSON(`/api/tags/?q=${encodeURIComponent(q)}`)
    } catch {
      this.#closeSuggestions()
      return
    }
    const results = listResults(data)
    this._items = results
      .filter((t) => !this._names.includes(t.name))
      .slice(0, this.maxSuggestionsValue)
    this.#renderSuggestions()
  }

  /** Dodaje tag (trim, dedupe case-sensitive), czyści input, zamyka podpowiedzi. */
  #add(rawName) {
    const name = (rawName ?? "").trim()
    if (!name) return
    this.inputTarget.value = ""
    this.#closeSuggestions()
    if (this._names.includes(name)) return // dedupe — już na liście
    this._names = [...this._names, name]
    this.#renderChips()
    this.#emit()
  }

  #remove(name) {
    this._names = this._names.filter((n) => n !== name)
    this.#renderChips()
    this.#emit()
  }

  // this.dispatch("change") fires "tag-chips:change" on this.element and bubbles
  // to window — a single emit reaches both listener styles used in this codebase.
  #emit() {
    const detail = { subcategory: this.subcategoryValue, names: this.names }
    this.dispatch("change", { detail })
  }

  /** Renderuje chipy wybranych tagów — createElement/textContent, zero innerHTML. */
  #renderChips() {
    this.chipsTarget.textContent = ""
    for (const name of this._names) {
      const chip = document.createElement("span")
      chip.className = "ls-chip"

      const label = document.createElement("span")
      label.className = "ls-chip__label"
      label.textContent = name
      chip.appendChild(label)

      const xBtn = document.createElement("button")
      xBtn.type = "button"
      xBtn.className = "ls-chip__x"
      xBtn.setAttribute("aria-label", "Usuń")
      xBtn.textContent = "×"
      xBtn.addEventListener("mousedown", (e) => {
        e.preventDefault()
        this.#remove(name)
      })
      chip.appendChild(xBtn)

      this.chipsTarget.appendChild(chip)
    }
  }

  /**
   * Renderuje listę podpowiedzi — createElement/textContent, zero innerHTML.
   * Najechanie (mouseenter) TYLKO przestawia podświetlenie, nigdy nie przebudowuje
   * listy: przebudowa pod kursorem odpina węzeł od DOM i mousedown gubi wybór.
   */
  #renderSuggestions() {
    this.suggestionsTarget.textContent = ""
    this._hiIndex = -1
    if (!this._items.length) {
      this.suggestionsTarget.hidden = true
      return
    }
    this._items.forEach((item, i) => {
      const row = document.createElement("div")
      row.className = "ls-combo__opt"

      const label = document.createElement("span")
      label.className = "ls-combo__opt-label"
      label.textContent = item.name
      row.appendChild(label)

      row.addEventListener("mousedown", (e) => {
        e.preventDefault()
        this.#add(item.name)
      })
      row.addEventListener("mouseenter", () => {
        this._hiIndex = i
        this.#highlight()
      })
      this.suggestionsTarget.appendChild(row)
    })
    this.suggestionsTarget.hidden = false
  }

  #highlight() {
    const rows = this.suggestionsTarget.querySelectorAll(".ls-combo__opt")
    rows.forEach((row, i) => row.classList.toggle("is-hi", i === this._hiIndex))
  }

  #closeSuggestions() {
    this.suggestionsTarget.hidden = true
    this.suggestionsTarget.textContent = ""
    this._items = []
    this._hiIndex = -1
  }
}
